import { ContactDataCreateOrUpdateCompanyDto, ContactDataCreateOrUpdatePersonDto, PartyType } from 'types/contacts';

/** The most contacts one merge takes, the kept one included. The backend refuses more. */
export const MAX_MERGED_CONTACTS = 10;

/** A kind of record a merge moves to the kept contact. EMPLOYEES are the persons a company employs, EMPLOYERS the companies a person is employed by. */
export enum PartyMergeRecordType {
    CASES = 'CASES',
    INVOICES = 'INVOICES',
    CONTRACTS = 'CONTRACTS',
    BD_EFFORTS = 'BD_EFFORTS',
    EXPERIENCE_RECORDS = 'EXPERIENCE_RECORDS',
    REFERRALS = 'REFERRALS',
    DEBRIEFINGS = 'DEBRIEFINGS',
    TRAININGS = 'TRAININGS',
    PARTY_ASSETS = 'PARTY_ASSETS',
    TASKS = 'TASKS',
    CORRESPONDENCE = 'CORRESPONDENCE',
    EMPLOYEES = 'EMPLOYEES',
    EMPLOYERS = 'EMPLOYERS',
    GROUPS = 'GROUPS',
}

/** A compliance field the merge combines by a fixed rule rather than by the user's choice. */
export enum PartyMergeComplianceField {
    VERIFICATION_STATUS = 'VERIFICATION_STATUS',
    VERIFICATION_NOTES = 'VERIFICATION_NOTES',
    RISK_PROFILE = 'RISK_PROFILE',
    RISK_PROFILE_NOTES = 'RISK_PROFILE_NOTES',
    RISK_FACTORS = 'RISK_FACTORS',
    MANUAL_BLOCKS = 'MANUAL_BLOCKS',
    PERSONA_NON_GRATA = 'PERSONA_NON_GRATA',
    POLITICALLY_EXPOSED_PERSON = 'POLITICALLY_EXPOSED_PERSON',
    UBO_NOT_NEEDED = 'UBO_NOT_NEEDED',
    SERVICE_AGREEMENT_NOT_NEEDED = 'SERVICE_AGREEMENT_NOT_NEEDED',
}

/** The rule that combines a compliance field's values. */
export enum PartyMergeComplianceRule {
    VERIFICATION = 'VERIFICATION',
    RISK_PROFILE = 'RISK_PROFILE',
    UNION = 'UNION',
    YES_WINS = 'YES_WINS',
    NO_WINS_SERVICE_AGREEMENT = 'NO_WINS_SERVICE_AGREEMENT',
    NO_WINS_UBO = 'NO_WINS_UBO',
    NOTES_BOTH = 'NOTES_BOTH',
}

export interface PartyMergeRecordCountDto {
    type: PartyMergeRecordType;
    count: number;
}

export interface PartyMergeLinkedRecordsDto {
    partyId: number;
    /** One count per record type, zero counts included. */
    linkedRecords: PartyMergeRecordCountDto[];
    /** Per record type, every type listed: how many of the party's records no other of the contacts links to. */
    unsharedRecords: PartyMergeRecordCountDto[];
    /** What converting the party to the other type removes; null unless the selection mixes persons and companies. */
    conversion: PartyMergeConversionDto | null;
}

/** A relation a conversion deletes. `incoming`: the converted party is the employee or the primary contact. */
export interface PartyMergeDroppedRelationDto {
    relationshipType: 'EMPLOYEE' | 'PRIMARY_CONTACT';
    incoming: boolean;
    partyId: number;
    name: string;
}

export interface PartyMergeConversionDto {
    /** The party's counts after the conversion, every type listed. */
    linkedRecords: PartyMergeRecordCountDto[];
    relations: PartyMergeDroppedRelationDto[];
    beneficialOwnerContracts: { id: number; name: string }[];
    /** Risk-factor codes. */
    riskFactors: string[];
    politicallyExposedPerson: boolean;
}

/** What the merged contact ends up with when it takes `partyType` and the others are converted first. */
export interface PartyMergeMergedRecordsDto {
    partyType: PartyType;
    /** Every type listed: each record once, however many of the contacts it is linked to. */
    linkedRecords: PartyMergeRecordCountDto[];
    /** Per record type, how many of those are linked to more than one of the contacts. */
    sharedRecords: PartyMergeRecordCountDto[];
    /** The compliance fields whose values differ once the others are converted. */
    compliance: PartyMergeComplianceDifferenceDto[];
}

export interface PartyMergeComplianceValuesDto {
    partyId: number;
    /** Classifier codes, "true"/"false" for a boolean, risk-factor or blocked-action codes, or note texts. */
    values: string[];
}

export interface PartyMergeComplianceDifferenceDto {
    field: PartyMergeComplianceField;
    /** Each party's values, in request order. */
    values: PartyMergeComplianceValuesDto[];
    /** The values the merge keeps. */
    result: string[];
    rule: PartyMergeComplianceRule;
}

/** GET /v1/parties/merge-preview */
export interface PartyMergePreviewDto {
    /** The parties in request order. */
    parties: PartyMergeLinkedRecordsDto[];
    /** Per record type, how many records are linked to more than one party. A type with none may be omitted. */
    sharedRecords: PartyMergeRecordCountDto[];
    /** One entry per party type among the parties. */
    mergedRecords: PartyMergeMergedRecordsDto[];
    /** Only the compliance fields whose stored values differ; `mergedRecords` has them after the conversions. */
    compliance: PartyMergeComplianceDifferenceDto[];
}

/** POST /v1/parties/{keptPartyId}/merge */
export interface PartyMergeRequestDto {
    /** In the order they merge. */
    mergedAwayPartyIds: number[];
    /** The kept party's full state after the merge, the same body PUT /v1/parties/{id} takes. */
    keptParty: ContactDataCreateOrUpdatePersonDto | ContactDataCreateOrUpdateCompanyDto;
}
